import React, { useCallback, useMemo } from "react";
import Divider from "@mui/material/Divider";
import { Base } from "../../framework/common/base";
import { useTranslation } from "react-i18next";
import { AppUtils } from "../../models/common/appUtils";
import {
    useSelectCalculatedWorkHoursAmountBySalaryRowTypeId,
    useSelectPaidWorkHoursAmountByDate,
} from "../../hooks/worktime/useSelectWorkHours";
import { useSelectSalaryPeriodByDate } from "../../hooks/worktime/useSelectSalaryPeriod";
import AccordionGroup, {
    AccordionDetailsItem,
} from "../framework/muiAccordion";
import { useAppSelector } from "../../framework/redux/hooks";
import { generatePath } from "react-router-dom";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { LinkButton } from "../../framework/common/linkButton";
import {
    additionCategories,
    normalWorkPaidCategories,
    overtimeCategories,
} from "../../models/workHours/workHoursItem";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import { OptionWorkShiftPlanningEnabled } from "../../models/common/consts";
import { useSelectPlannedWorkShiftsDurationByDate } from "../../hooks/worktime/useSelectPlannedWorkShifts";
import { StrMeasureUnit } from "../../models/common/enums";
import { useShowWorkShiftEndTimesInMobile } from "../../hooks/framework/useShowWorkShiftEndTimesInMobile";

interface WorklogSectionsProps {
    dateString: string;
}

export const WorklogSections = ({ dateString }: WorklogSectionsProps) => {
    const weekNumber = useMemo(
        () => Base.getWeekNumber(dateString),
        [dateString]
    );
    const { t } = useTranslation();

    const salaryRowTypes = useAppSelector(
        (state) => state.workShiftTimeSlot.salaryRowTypes
    );

    const startOfWeek = Base.dateToDateJSON(
        Base.dateStartOf(dateString, "week")
    );
    const endOfWeek = Base.dateToDateJSON(Base.dateEndOf(dateString, "week"));

    const selectedSalaryPeriodItem = useSelectSalaryPeriodByDate(dateString);

    const weekMinutes = useSelectPaidWorkHoursAmountByDate(
        startOfWeek,
        endOfWeek
    );

    const weekWorkHours = useSelectCalculatedWorkHoursAmountBySalaryRowTypeId(
        startOfWeek,
        endOfWeek
    );

    const dayMinutes = useSelectPaidWorkHoursAmountByDate(
        dateString,
        dateString
    );

    const dayWorkHours = useSelectCalculatedWorkHoursAmountBySalaryRowTypeId(
        dateString,
        dateString
    );

    const salaryPeriodMinutes = useSelectPaidWorkHoursAmountByDate(
        selectedSalaryPeriodItem?.startDate,
        selectedSalaryPeriodItem?.endDate
    );

    const salaryPeriodWorkHours =
        useSelectCalculatedWorkHoursAmountBySalaryRowTypeId(
            selectedSalaryPeriodItem?.startDate,
            selectedSalaryPeriodItem?.endDate
        );

    const salaryRowTypeMap = useMemo(
        () => new Map((salaryRowTypes ?? []).map((s) => [s.id, s])),
        [salaryRowTypes]
    );

    const dayShiftMinutes = useSelectPlannedWorkShiftsDurationByDate(
        dateString,
        dateString
    );

    const weekShiftMinutes = useSelectPlannedWorkShiftsDurationByDate(
        startOfWeek,
        endOfWeek
    );

    const salaryPeriodShiftMinutes = useSelectPlannedWorkShiftsDurationByDate(
        selectedSalaryPeriodItem?.startDate,
        selectedSalaryPeriodItem?.endDate
    );

    const getSectionContent = useCallback(
        (workHours: { [k: string]: number }) => {
            const categories = [
                normalWorkPaidCategories(),
                additionCategories(),
                overtimeCategories(),
            ];

            const grouped: { [key: number]: JSX.Element[] } = {};

            const srts = Object.keys(workHours)
                .map((k) => salaryRowTypeMap.get(k))
                .filter(Boolean);

            srts.forEach((srt) => {
                let idx = categories.findIndex((c) =>
                    c.some((cat) => srt.hourBookingCategories.includes(cat))
                );

                // If idx not found, it will be in last group.
                idx = idx === -1 ? categories.length : idx;
                grouped[idx] ||= [];

                // Group items by idx
                // Push each item to their group
                grouped[idx].push(
                    <AccordionDetailsItem
                        key={srt.id}
                        title={srt.name}
                        hours={AppUtils.getDisplayedNumberBasedOnUnit(
                            workHours[srt.id],
                            srt.measureUnit
                        )}
                    />
                );
            });

            let retVal: JSX.Element[] = [];

            // Return each group separated by Divider
            Object.keys(grouped)
                .sort()
                .forEach((k, i) => {
                    const arr = grouped[k];

                    if (arr.length > 0) {
                        if (retVal.length > 0) {
                            retVal.push(
                                <Divider
                                    key={`divider-${i}`}
                                    sx={{ mt: 1.5, mb: 0.5 }}
                                />
                            );
                        }

                        retVal = [...retVal, ...arr];
                    }
                });

            return retVal;
        },
        [salaryRowTypeMap]
    );

    const salaryPeriodTitle = useMemo(
        () =>
            `${t("workTime.salaryPeriod")} (${
                new Date(selectedSalaryPeriodItem?.startDate).getFullYear() !==
                new Date(selectedSalaryPeriodItem?.endDate).getFullYear()
                    ? Base.dateToDateStr(selectedSalaryPeriodItem?.startDate)
                    : Base.dateToShortDateStr(
                          selectedSalaryPeriodItem?.startDate
                      )
            } -
                ${Base.dateToDateStr(selectedSalaryPeriodItem?.endDate)})`,
        [
            selectedSalaryPeriodItem?.startDate,
            selectedSalaryPeriodItem?.endDate,
            t,
        ]
    );

    const dayContent = useMemo(
        () => getSectionContent(dayWorkHours),
        [getSectionContent, dayWorkHours]
    );
    const weekContent = useMemo(
        () => getSectionContent(weekWorkHours),
        [getSectionContent, weekWorkHours]
    );
    const salaryPeriodContent = useMemo(
        () => getSectionContent(salaryPeriodWorkHours),
        [getSectionContent, salaryPeriodWorkHours]
    );

    return (
        <AccordionGroup
            items={[
                {
                    key: "day",
                    header: (
                        <>
                            {`${t("time.day")} ${Base.dateToDateStr(
                                dateString
                            )}`}
                            <LinkButton
                                color="success"
                                to={generatePath("/worklog/:date", {
                                    date: dateString,
                                })}
                                sx={{
                                    display: "inline",
                                    lineHeight: "normal",
                                }}
                            >
                                <KeyboardArrowRightIcon fontSize="large" />
                            </LinkButton>
                        </>
                    ),
                    subheader: AppUtils.getDurationStrByDurationMin(
                        dayMinutes,
                        true
                    ),
                    content: [
                        <PlannedHoursDetailsItem
                            key="planned"
                            minutes={dayShiftMinutes}
                            divider={dayContent.length > 0}
                        />,
                        ...dayContent,
                    ],
                },
                {
                    header: `${t("time.week")} (${weekNumber})`,
                    subheader: AppUtils.getDurationStrByDurationMin(
                        weekMinutes,
                        true
                    ),
                    content: [
                        <PlannedHoursDetailsItem
                            key="planned"
                            minutes={weekShiftMinutes}
                            divider={weekContent.length > 0}
                        />,
                        ...weekContent,
                    ],
                },
                selectedSalaryPeriodItem && salaryPeriodMinutes
                    ? {
                        header: salaryPeriodTitle,
                        subheader: AppUtils.getDurationStrByDurationMin(
                            salaryPeriodMinutes,
                            true
                        ),
                        content: [
                            <PlannedHoursDetailsItem
                                key="planned"
                                minutes={salaryPeriodShiftMinutes}
                                divider={salaryPeriodContent.length > 0}
                            />,
                            ...salaryPeriodContent,
                        ],
                    }
                    : null,
            ].filter(Boolean)}
        />
    );
};

interface PlannedHoursDetailsItemProps {
    minutes: number;
    divider: boolean;
}

const PlannedHoursDetailsItem = ({ minutes, divider }: PlannedHoursDetailsItemProps) => {
    const [plannedShiftsEnabled] = useAppOptionValueEquals(
        OptionWorkShiftPlanningEnabled,
        true
    );
    const showEndTimes = useShowWorkShiftEndTimesInMobile();

    const { t } = useTranslation();

    if (!plannedShiftsEnabled || !minutes || !showEndTimes) {
        return null;
    }

    return (
        <>
            <AccordionDetailsItem
                title={t("workTime.calendar.plannedHours")}
                hours={AppUtils.getDisplayedNumberBasedOnUnit(
                    minutes,
                    StrMeasureUnit.Hour
                )}
                color="blue.main"
            />
            {divider ? (
                <Divider sx={{ mt: 1.5, mb: 0.5, borderWidth: "2px" }} />
            ) : null}
        </>
    );
};